type Item = {
  alt?: string
  provider?: string
  src: string
}

type MediaType = 'image' | 'video' | 'unknown' // | '360' | 'youtube' | 'vimeo'

type MediaItem = Item & {
  index: number
  type: MediaType
}

type ImageOptions = {
  height?: number
  quality?: number
  stripmetadata?: number
  stripcolorprofile?: number
  width?: number
  imageSize?: string
}

type BaseGalleryOptions = {
  dots?: boolean
  navigation?: boolean
  thumbs?: boolean
  thumbsDirection?: 'horizontal' | 'vertical'
  thumbsTrigger?: 'click' | 'mouseover'
  variant?: string
  XofY?: boolean
  imageSize?: string
}

export type GalleryOptions = BaseGalleryOptions & {
  behavior?: 'smooth' | 'instant'
  fullscreen?: boolean
  fullscreenOptions: BaseGalleryOptions
  index?: number
  media?: Item[]
}

export function useUiMediaGallery(name: string, options: GalleryOptions | null = null) {
  const defaultOptions: GalleryOptions = {
    behavior: 'smooth',
    dots: true,
    fullscreen: true,
    fullscreenOptions: {
      dots: true,
      navigation: true,
      thumbs: true,
      thumbsDirection: 'vertical',
      thumbsTrigger: 'click',
      XofY: true,
    },
    index: 1,
    navigation: true,
    thumbsDirection: 'horizontal',
    thumbsTrigger: 'click',
    thumbs: true,
    XofY: true,
    imageSize: 'M standaard',
  }

  const galleryOptions = useState<GalleryOptions>(`${name}__gallery-options`, () => ({
    ...defaultOptions,
  }))

  if (options) {
    galleryOptions.value = {
      ...defaultOptions,
      ...options,
      fullscreenOptions: {
        ...defaultOptions.fullscreenOptions,
        ...(options?.fullscreenOptions || {}),
      },
    }
  }

  const galleryItems = useState<MediaItem[]>(`${name}__gallery-items`, () => [])
  const activeItem = useState<MediaItem | null>(`${name}__gallery-active-item`, () => null)

  const gallery = useState<HTMLElement | null>('gallery', () => null)

  function setItem(index: number, behavior: ScrollBehavior = 'instant') {
    const newItem = galleryItems.value.find(
      (item: MediaItem) => item?.index === index,
    )

    if (!newItem) {
      console.error('Can\'t find galleryItem with index of: ', index)
      return
    }

    const elm = gallery.value?.querySelector(
      `[data-item="${index}"]`,
    ) as HTMLElement

    gallery.value?.scroll({
      top: 0,
      left: elm?.offsetLeft || 0,
      behavior,
    })

    activeItem.value = newItem
  }

  function getImageFromProvider(item: MediaItem, options?: ImageOptions): string {
    if (!item) {
      return ''
    }

    const params: string[] = []

    switch (item.provider) {
      case 'default':
      case 'xcdn': {
        const { size, padding, exar } = getImageParams({
          imageSize: options?.imageSize,
          width: options?.width,
          height: options?.height,
          index: item.index,
        })

        params.push(size)
        params.push(`t:5:ffffff`)
        params.push(`q:${options?.quality || 100}`)
        params.push(`sm:${options?.stripmetadata || 1}`)
        params.push(`scp:${options?.stripcolorprofile || 1}`)
        params.push(exar)
        params.push(`bg:ffffff`)
        params.push(padding)

        return `${item.src}?${params.join('/')}`
      }
      case 'scayle': {
        if (options?.width) {
          params.push(`width=${options.width}`)
        }

        if (options?.height) {
          params.push(`heigth=${options.height}`)
        }

        if (options?.quality) {
          params.push(`quality=${options.quality}`)
        }
        return `${item.src}${params.length ? '?' + params.join('&') : ''}`
      }
      case 'contentful': {
        params.push('f=faces')
        params.push('fit=fill')

        if (options?.width) {
          params.push(`w=${options.width}`)
        }

        if (options?.height) {
          params.push(`h=${options.height}`)
        }

        return `${item.src}${params.length ? '?' + params.join('&') : ''}`
      }
      default:
        return item.src
    }
  }

  if (options?.media?.length) {
    galleryItems.value = options?.media.map((item: Item, index: number): MediaItem => {
      const types: Record<string, MediaType> = {
        '.jpg': 'image',
        '.jpeg': 'image',
        '.png': 'image',
        '.webp': 'image',
        '.avif': 'image',
        '.gif': 'image',

        '.mp4': 'video',
        'youtu.be': 'video',
        'youtube': 'video',
        'vimeo': 'video',
      }

      const typeKey: string
        = Object.keys(types).find(type => item.src.includes(type)) || ''

      const type = typeKey ? types[typeKey] : 'unknown'

      return {
        ...item,
        type,
        index: index + 1,
        provider: item.provider || 'default',
      }
    })

    activeItem.value
      = galleryItems.value.find((item: MediaItem) => item?.index === galleryOptions.value.index) || null
  }

  return {
    gallery,
    galleryItems,
    galleryOptions,
    activeItem,
    setItem,
    getImageFromProvider,
  }
}

export type { Item, MediaItem, ImageOptions }
